import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Card, Button, Spinner, PageHeader, StatCard } from '../components/common';
import { formatDataOra, formatBytes } from '../utils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

const platiAPI = {
  getTaxe:      ()      => api.get('/plati/taxe').then(r => r.data),
  initiaza:     (d)     => api.post('/plati/initiaza', d),
  getMele:      (p)     => api.get('/plati/mele', { params: p }).then(r => r.data),
  getToate:     ()      => api.get('/plati/toate').then(r => r.data),
  getChitanta:  (id)    => api.get(`/plati/${id}/chitanta`).then(r => r.data),
  getStatus:    (ord)   => api.get(`/plati/status/${ord}`).then(r => r.data.data),
};

const CATEGORIE_LABELS = {
  impozit:     'Impozite',
  taxa:        'Taxe',
  amenda:      'Amenzi',
  aviz:        'Avize',
  autorizatie: 'Autorizații',
  altul:       'Altele',
};

const STATUS_PLATA = {
  initializata: { label: 'În așteptare', color: '#f59e0b', bg: '#fef9c3' },
  in_procesare: { label: 'În procesare', color: '#3b82f6', bg: '#eff6ff' },
  confirmata:   { label: 'Confirmată',   color: '#059669', bg: '#d1fae5' },
  esuata:       { label: 'Eșuată',       color: '#dc2626', bg: '#fee2e2' },
  anulata:      { label: 'Anulată',      color: '#6b7280', bg: '#f3f4f6' },
  rambursata:   { label: 'Rambursată',   color: '#7c3aed', bg: '#ede9fe' },
};

export default function PlatiPage() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('plateste');
  const [searchParams] = useSearchParams();

  // Detectăm întoarcerea de la EuPlătesc
  const orderId = searchParams.get('orderId');

  return (
    <Layout>
      <PageHeader title="Plăți online" subtitle="Plătește taxe și impozite locale" />

      <div style={tabs}>
        {[
          ['plateste',  'Plătește'],
          ['istoricul', 'Istoricul meu'],
          ...(isAdmin ? [['toate', 'Toate plățile'], ['taxe', 'Gestionare taxe']] : []),
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...tabBtn, ...(tab === key ? tabActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {orderId && <ConfirmareRetur orderId={orderId} />}

      {tab === 'plateste'  && <TabPlateste user={user} />}
      {tab === 'istoricul' && <TabIstoricul />}
      {tab === 'toate'     && isAdmin && <TabToate />}
      {tab === 'taxe'      && isAdmin && <TabTaxe />}
    </Layout>
  );
}

// ── Confirmare întoarcere de la EuPlătesc ─────────────────────
function ConfirmareRetur({ orderId }) {
  const { data, isLoading } = useQuery(
    ['plata-status', orderId],
    () => platiAPI.getStatus(orderId),
    { refetchInterval: 5000 }
  );

  if (isLoading) return null;
  if (!data) return null;

  const cfg = STATUS_PLATA[data.status] || STATUS_PLATA.in_procesare;

  return (
    <div style={{ ...returBox, backgroundColor: cfg.bg, borderColor: cfg.color }}>
      <div style={{ fontSize: '32px' }}>
        {data.status === 'confirmata' ? '✅' : data.status === 'esuata' ? '❌' : '⏳'}
      </div>
      <div>
        <strong style={{ color: cfg.color }}>{cfg.label}</strong>
        <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#555' }}>
          {data.status === 'confirmata'
            ? `Plata de ${data.suma} RON a fost procesată cu succes. Chitanța ${data.chitanta_serie}/${data.chitanta_numar} este disponibilă.`
            : data.status === 'esuata'
            ? `Plata a eșuat: ${data.motiv_esec || 'eroare necunoscută'}`
            : 'Plata se procesează. Veți fi notificat prin email.'}
        </p>
        {data.status === 'confirmata' && (
          <button
            onClick={async () => {
              const r = await platiAPI.getChitanta(data.id);
              window.open(r.url, '_blank');
            }}
            style={{ ...btnMic, marginTop: '8px' }}
          >
            📄 Descarcă chitanța
          </button>
        )}
      </div>
    </div>
  );
}

// ── Tab: Plătește ─────────────────────────────────────────────
function TabPlateste({ user }) {
  const [taxaSelectata, setTaxaSelectata] = useState(null);
  const [pasActual, setPasActual]         = useState(1); // 1: selectare, 2: date, 3: redirect
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const isMobile = useWindowWidth() < 640;

  const { data: taxeData, isLoading } = useQuery('taxe', platiAPI.getTaxe);
  const grupe = taxeData?.grupe || {};
  const taxe  = taxeData?.data  || [];

  const mutation = useMutation(platiAPI.initiaza, {
    onSuccess: (r) => {
      toast.info('Redirecționare către EuPlătesc...');
      setTimeout(() => { window.location.href = r.data.redirectUrl; }, 1500);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Eroare inițiere plată'),
  });

  function onSubmit(formData) {
    mutation.mutate({
      taxaId:       taxaSelectata?.id,
      suma:         parseFloat(formData.suma),
      descriere:    `${taxaSelectata?.denumire || formData.descriere} — ${process.env.REACT_APP_PRIMARIA || 'Primărie'}`,
      platitorNume:  formData.platitorNume  || `${user?.prenume} ${user?.nume}`,
      platitorEmail: formData.platitorEmail || user?.email,
      platitorPhone: formData.platitorPhone || user?.phone,
      platitorCnp:   formData.platitorCnp,
    });
  }

  if (isLoading) return <Spinner />;

  return (
    <div>
      {/* Pas 1: Selectare taxă */}
      <Card title="Selectează taxa de plată">
        {Object.entries(grupe).map(([categorie, taxeLista]) => (
          <div key={categorie} style={{ marginBottom: '16px' }}>
            <div style={catLabel}>{CATEGORIE_LABELS[categorie] || categorie}</div>
            <div style={taxeGrid}>
              {taxeLista.map(taxa => (
                <button
                  key={taxa.id}
                  onClick={() => { setTaxaSelectata(taxa); setPasActual(2); }}
                  style={{ ...taxaBtn, ...(taxaSelectata?.id === taxa.id ? taxaBtnActiv : {}) }}
                >
                  <span style={taxaNume}>{taxa.denumire}</span>
                  <span style={taxaSuma}>
                    {taxa.suma_fixa
                      ? `${taxa.suma_fixa} RON`
                      : taxa.suma_min
                      ? `de la ${taxa.suma_min} RON`
                      : 'Sumă variabilă'
                    }
                  </span>
                  {taxa.descriere && <span style={taxaDesc}>{taxa.descriere}</span>}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Plată personalizată */}
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
          <button
            onClick={() => { setTaxaSelectata(null); setPasActual(2); }}
            style={{ ...taxaBtn, ...(taxaSelectata === null && pasActual === 2 ? taxaBtnActiv : {}) }}
          >
            <span style={taxaNume}>Altă sumă</span>
            <span style={taxaDesc}>Introduceți suma manual</span>
          </button>
        </div>
      </Card>

      {/* Pas 2: Completare date */}
      {pasActual >= 2 && (
        <Card title={`Date plată${taxaSelectata ? ` — ${taxaSelectata.denumire}` : ''}`}>
          <form onSubmit={handleSubmit(onSubmit)} style={formCol}>

            {/* Sumă */}
            <div style={fld}>
              <label style={lbl}>Sumă (RON) *</label>
              {taxaSelectata?.suma_fixa
                ? <div style={sumaFixa}>{taxaSelectata.suma_fixa} RON</div>
                : (
                  <input
                    type="number"
                    step="0.01"
                    min={taxaSelectata?.suma_min || 0.01}
                    max={taxaSelectata?.suma_max || 99999}
                    style={inp}
                    placeholder="ex: 150.00"
                    {...register('suma', {
                      required: 'Suma este obligatorie',
                      min: { value: 0.01, message: 'Suma minimă este 0.01 RON' }
                    })}
                  />
                )
              }
              {errors.suma && <span style={errMsg}>{errors.suma.message}</span>}
            </div>

            {!taxaSelectata && (
              <div style={fld}>
                <label style={lbl}>Descriere plată *</label>
                <input type="text" style={inp}
                  placeholder="ex: Impozit auto număr B-123-ABC"
                  {...register('descriere', { required: 'Descrierea este obligatorie' })} />
                {errors.descriere && <span style={errMsg}>{errors.descriere.message}</span>}
              </div>
            )}

            {/* Date plătitor */}
            <div style={fld}>
              <label style={lbl}>Nume complet</label>
              <input type="text" style={inp}
                defaultValue={user ? `${user.prenume} ${user.nume}` : ''}
                {...register('platitorNume')} />
            </div>

            <div style={{ ...s2col, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
              <div style={fld}>
                <label style={lbl}>Email confirmare</label>
                <input type="email" style={inp}
                  defaultValue={user?.email || ''}
                  {...register('platitorEmail')} />
              </div>
              <div style={fld}>
                <label style={lbl}>Telefon</label>
                <input type="tel" style={inp}
                  defaultValue={user?.phone || ''}
                  placeholder="07xx xxx xxx"
                  {...register('platitorPhone')} />
              </div>
            </div>

            <div style={fld}>
              <label style={lbl}>CNP (opțional)</label>
              <input type="text" style={inp} maxLength={13}
                placeholder="13 cifre"
                {...register('platitorCnp', {
                  pattern: { value: /^\d{13}$/, message: 'CNP trebuie să aibă exact 13 cifre' }
                })} />
              {errors.platitorCnp && <span style={errMsg}>{errors.platitorCnp.message}</span>}
            </div>

            {/* Preview sumă */}
            <div style={sumaPreview}>
              <span>Total de plată:</span>
              <strong style={{ fontSize: '22px', color: '#1a1a2e' }}>
                {taxaSelectata?.suma_fixa || watch('suma') || '0'} RON
              </strong>
            </div>

            {/* Info securitate */}
            <div style={secInfo}>
              🔒 Plata este procesată securizat prin <strong>EuPlătesc</strong> — platforma oficială a Guvernului României.
              Datele cardului nu sunt stocate de primărie.
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button type="submit" disabled={mutation.isLoading} style={{ flex: 1 }}>
                {mutation.isLoading ? 'Se inițiază...' : '💳 Continuă spre plată'}
              </Button>
              <Button onClick={() => setPasActual(1)} variant="ghost">Înapoi</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Istoricul meu ────────────────────────────────────────
function TabIstoricul() {
  const { data, isLoading } = useQuery('plati-mele', () => platiAPI.getMele({}));

  if (isLoading) return <Spinner />;

  const plati = data?.data || [];

  return (
    <Card title={`Istoricul plăților (${data?.pagination?.total || 0})`}>
      {plati.length === 0
        ? <p style={{ color: '#888', textAlign: 'center', padding: '32px' }}>Nu ai plăți înregistrate</p>
        : plati.map(p => {
          const cfg = STATUS_PLATA[p.status] || STATUS_PLATA.initializata;
          return (
            <div key={p.id} style={platRow}>
              <div style={platMain}>
                <span style={{ ...platStatus, color: cfg.color, backgroundColor: cfg.bg }}>
                  {cfg.label}
                </span>
                <span style={platDescr}>{p.descriere}</span>
                <span style={platMeta}>
                  {p.taxa_denumire || ''} · {formatDataOra(p.initializata_la)}
                  {p.numar_cerere && ` · Cerere ${p.numar_cerere}`}
                </span>
              </div>
              <div style={platRight}>
                <span style={platSuma}>{p.suma} RON</span>
                {p.status === 'confirmata' && p.chitanta_numar && (
                  <span style={platChitanta}>Chit. {p.chitanta_serie}/{p.chitanta_numar}</span>
                )}
                {p.status === 'confirmata' && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await platiAPI.getChitanta(p.id);
                        window.open(r.url, '_blank');
                      } catch { toast.error('Eroare descărcare chitanță'); }
                    }}
                    style={btnMic}
                  >📄 Chitanță</button>
                )}
              </div>
            </div>
          );
        })
      }
    </Card>
  );
}

// ── Tab: Toate plățile (admin) ────────────────────────────────
function TabToate() {
  const { data, isLoading } = useQuery('plati-toate', platiAPI.getToate);
  if (isLoading) return <Spinner />;

  const stats = data?.stats || {};
  const plati = data?.data  || [];

  return (
    <div>
      <div style={grid4}>
        <StatCard label="Total încasat"   value={`${parseFloat(stats.total_incasat || 0).toFixed(2)} RON`} color="#059669" />
        <StatCard label="Confirmate"      value={stats.confirmate   || 0} color="#059669" />
        <StatCard label="În așteptare"    value={stats.in_asteptare || 0} color="#f59e0b" />
        <StatCard label="Eșuate"          value={stats.esuate       || 0} color="#dc2626" />
      </div>

      <Card title={`Toate plățile (${plati.length})`}>
        <div style={thRow}>
          {['Dată','Plătitor','Descriere','Sumă','Status','Chitanță'].map(h => (
            <span key={h} style={th}>{h}</span>
          ))}
        </div>
        {plati.map(p => {
          const cfg = STATUS_PLATA[p.status] || STATUS_PLATA.initializata;
          return (
            <div key={p.id} style={tdRow}>
              <span style={td}>{formatDataOra(p.initializata_la)}</span>
              <span style={td}>{p.platitor_nume || p.user_email || '—'}</span>
              <span style={{ ...td, flex: 2 }}>{p.descriere}</span>
              <span style={{ ...td, fontWeight: '700' }}>{p.suma} RON</span>
              <span style={{ ...td, color: cfg.color, fontWeight: '600' }}>{cfg.label}</span>
              <span style={td}>{p.chitanta_numar ? `${p.chitanta_serie}/${p.chitanta_numar}` : '—'}</span>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ── Tab: Gestionare taxe (admin) ──────────────────────────────
function TabTaxe() {
  const qc = useQueryClient();
  const [mod, setMod] = useState(null); // null | 'add' | 'edit'
  const [taxaEditata, setTaxaEditata] = useState(null);
  const { register, handleSubmit, reset } = useForm();

  const { data: taxeData, isLoading } = useQuery(
    'taxe-admin-all',
    () => api.get('/plati/taxe/all').then(r => r.data.data)
  );
  const taxe = taxeData || [];

  const invalidate = () => { qc.invalidateQueries('taxe-admin-all'); qc.invalidateQueries('taxe'); };

  const createMut = useMutation(
    (d) => api.post('/plati/taxe', d),
    {
      onSuccess: () => { toast.success('Taxă adăugată'); invalidate(); setMod(null); reset(); },
      onError: (e) => toast.error(e.response?.data?.message || e.response?.data?.errors?.[0]?.msg || 'Eroare creare'),
    }
  );

  const updateMut = useMutation(
    ({ id, d }) => api.put(`/plati/taxe/${id}`, d),
    {
      onSuccess: () => { toast.success('Taxă actualizată'); invalidate(); setMod(null); reset(); },
      onError: (e) => toast.error(e.response?.data?.message || e.response?.data?.errors?.[0]?.msg || 'Eroare salvare'),
    }
  );

  const toggleMut = useMutation(
    (id) => api.patch(`/plati/taxe/${id}/toggle`),
    {
      onSuccess: (r) => { invalidate(); toast.info(r.data.data.activ ? 'Taxă activată' : 'Taxă dezactivată'); },
      onError: () => toast.error('Eroare'),
    }
  );

  function deschideEditare(taxa) {
    setTaxaEditata(taxa);
    setMod('edit');
    reset({
      denumire:   taxa.denumire,
      descriere:  taxa.descriere   || '',
      categorie:  taxa.categorie,
      sumaFixa:   taxa.suma_fixa   != null ? taxa.suma_fixa   : '',
      sumaMin:    taxa.suma_min    != null ? taxa.suma_min    : '',
      sumaMax:    taxa.suma_max    != null ? taxa.suma_max    : '',
      tvaProcent: taxa.tva_procent != null ? taxa.tva_procent : 0,
      contIban:   taxa.cont_iban   || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function onSubmit(d) {
    if (mod === 'edit') updateMut.mutate({ id: taxaEditata.id, d });
    else               createMut.mutate(d);
  }

  const activeCount   = taxe.filter(t => t.activ).length;
  const inactiveCount = taxe.length - activeCount;
  const isSaving      = createMut.isLoading || updateMut.isLoading;

  if (isLoading) return <Spinner />;

  return (
    <div>
      {/* Stats + buton */}
      <div style={txTopBar}>
        <div style={txStats}>
          <div style={txStatBox}>
            <span style={txStatVal}>{taxe.length}</span>
            <span style={txStatLbl}>Total</span>
          </div>
          <div style={txStatBox}>
            <span style={{ ...txStatVal, color: '#059669' }}>{activeCount}</span>
            <span style={txStatLbl}>Active</span>
          </div>
          <div style={txStatBox}>
            <span style={{ ...txStatVal, color: '#6b7280' }}>{inactiveCount}</span>
            <span style={txStatLbl}>Inactive</span>
          </div>
        </div>
        <Button onClick={() => { setMod(mod === 'add' ? null : 'add'); setTaxaEditata(null); reset({}); }}>
          {mod === 'add' ? '✕ Anulează' : '+ Taxă nouă'}
        </Button>
      </div>

      {/* Formular adăugare / editare */}
      {mod && (
        <Card title={mod === 'add' ? 'Adaugă taxă nouă' : `Editează: ${taxaEditata?.denumire}`}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={txForm}>
              {mod === 'add' && (
                <div style={fld}>
                  <label style={lbl}>Cod unic * <span style={{ color: '#9ca3af', fontWeight: 400 }}>(ex: TAXA_PARCARE)</span></label>
                  <input type="text" style={inp} placeholder="TAXA_PARCARE"
                    {...register('cod', { required: true })} />
                </div>
              )}
              <div style={fld}>
                <label style={lbl}>Denumire *</label>
                <input type="text" style={inp} placeholder="ex: Taxă parcare rezidențială"
                  {...register('denumire', { required: true })} />
              </div>
              <div style={fld}>
                <label style={lbl}>Categorie *</label>
                <select style={inp} {...register('categorie', { required: true })}>
                  {Object.entries(CATEGORIE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div style={fld}>
                <label style={lbl}>Sumă fixă RON</label>
                <input type="number" step="0.01" min="0" style={inp}
                  placeholder="Lasă gol pentru sumă variabilă"
                  {...register('sumaFixa')} />
              </div>
              <div style={fld}>
                <label style={lbl}>Sumă minimă RON</label>
                <input type="number" step="0.01" min="0" style={inp} placeholder="0.00"
                  {...register('sumaMin')} />
              </div>
              <div style={fld}>
                <label style={lbl}>Sumă maximă RON</label>
                <input type="number" step="0.01" min="0" style={inp} placeholder="0.00"
                  {...register('sumaMax')} />
              </div>
              <div style={fld}>
                <label style={lbl}>TVA %</label>
                <input type="number" step="0.01" min="0" max="100" style={inp} placeholder="0"
                  {...register('tvaProcent')} />
              </div>
              <div style={fld}>
                <label style={lbl}>IBAN cont colectare</label>
                <input type="text" style={inp} placeholder="RO49AAAA1B31007593840000"
                  {...register('contIban')} />
              </div>
              <div style={{ ...fld, gridColumn: '1/-1' }}>
                <label style={lbl}>Descriere</label>
                <textarea rows={2} style={{ ...inp, resize: 'vertical' }}
                  placeholder="Descriere opțională..."
                  {...register('descriere')} />
              </div>
              <div style={{ gridColumn: '1/-1', display: 'flex', gap: '12px' }}>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Se salvează...' : mod === 'add' ? 'Adaugă taxa' : 'Salvează modificările'}
                </Button>
                <Button variant="ghost" onClick={() => { setMod(null); reset({}); }} type="button">
                  Anulează
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}

      {/* Lista taxe */}
      <Card title={`Taxe configurate (${taxe.length})`}>
        {taxe.length === 0
          ? <p style={{ color: '#888', textAlign: 'center', padding: '32px' }}>Nicio taxă configurată. Apasă «+ Taxă nouă».</p>
          : (
            <>
              <div style={txHeader}>
                <span style={{ ...txTh, flex: 1.3 }}>Cod</span>
                <span style={{ ...txTh, flex: 3 }}>Denumire</span>
                <span style={{ ...txTh, flex: 1.3 }}>Categorie</span>
                <span style={{ ...txTh, flex: 1.5 }}>Sumă</span>
                <span style={{ ...txTh, flex: 0.8 }}>Status</span>
                <span style={{ ...txTh, flex: 1.8 }}>Acțiuni</span>
              </div>
              {taxe.map(taxa => (
                <div key={taxa.id} style={{ ...txRow, opacity: taxa.activ ? 1 : 0.6 }}>
                  <span style={{ ...txTd, flex: 1.3, fontFamily: 'monospace', fontSize: '11px', color: '#888' }}>
                    {taxa.cod}
                  </span>
                  <div style={{ ...txTd, flex: 3 }}>
                    <div style={{ fontWeight: '500', color: '#1a1a2e', fontSize: '14px' }}>{taxa.denumire}</div>
                    {taxa.descriere && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{taxa.descriere}</div>}
                    {taxa.cont_iban && <div style={{ fontSize: '11px', color: '#9ca3af' }}>IBAN: {taxa.cont_iban}</div>}
                  </div>
                  <span style={{ ...txTd, flex: 1.3 }}>
                    <span style={txCatBadge}>{CATEGORIE_LABELS[taxa.categorie]}</span>
                  </span>
                  <div style={{ ...txTd, flex: 1.5 }}>
                    <span style={{ fontWeight: '600', color: '#2d2d7e', fontSize: '14px' }}>
                      {taxa.suma_fixa != null
                        ? `${parseFloat(taxa.suma_fixa).toFixed(2)} RON`
                        : taxa.suma_min != null
                        ? `${parseFloat(taxa.suma_min).toFixed(2)}–${taxa.suma_max ? parseFloat(taxa.suma_max).toFixed(2) : '∞'} RON`
                        : 'Variabilă'}
                    </span>
                    {taxa.tva_procent > 0 && (
                      <div style={{ fontSize: '11px', color: '#888' }}>+{taxa.tva_procent}% TVA</div>
                    )}
                  </div>
                  <span style={{ ...txTd, flex: 0.8 }}>
                    <span style={{ ...txStatusBadge, color: taxa.activ ? '#059669' : '#6b7280', backgroundColor: taxa.activ ? '#d1fae5' : '#f3f4f6' }}>
                      {taxa.activ ? 'Activă' : 'Inactivă'}
                    </span>
                  </span>
                  <div style={{ ...txTd, flex: 1.8, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => deschideEditare(taxa)} style={txActBtn}>
                      ✏️ Editează
                    </button>
                    <button
                      onClick={() => toggleMut.mutate(taxa.id)}
                      disabled={toggleMut.isLoading}
                      style={{ ...txActBtn, color: taxa.activ ? '#dc2626' : '#059669', borderColor: taxa.activ ? '#fca5a5' : '#6ee7b7' }}
                    >
                      {taxa.activ ? 'Dezactivează' : 'Activează'}
                    </button>
                  </div>
                </div>
              ))}
            </>
          )
        }
      </Card>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const tabs     = { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #f0f0f0' };
const tabBtn   = { padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#888', borderBottom: '2px solid transparent', marginBottom: '-2px' };
const tabActive = { color: '#2d2d7e', borderBottomColor: '#2d2d7e' };
const returBox = { display: 'flex', gap: '16px', alignItems: 'flex-start', padding: '16px', borderRadius: '12px', border: '2px solid', marginBottom: '20px' };
const catLabel = { fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' };
const taxeGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' };
const taxaBtn  = { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', backgroundColor: '#fff', textAlign: 'left', transition: 'all .15s' };
const taxaBtnActiv = { borderColor: '#2d2d7e', backgroundColor: '#eff6ff' };
const taxaNume = { fontSize: '13px', fontWeight: '600', color: '#1a1a2e' };
const taxaSuma = { fontSize: '16px', fontWeight: '700', color: '#2d2d7e' };
const taxaDesc = { fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' };
const formCol  = { display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '480px' };
const fld      = { display: 'flex', flexDirection: 'column', gap: '4px' };
const lbl      = { fontSize: '13px', fontWeight: '500', color: '#555' };
const inp      = { padding: '10px 12px', fontSize: '16px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const errMsg   = { fontSize: '12px', color: '#dc2626' };
const s2col    = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };
const sumaFixa = { padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', fontSize: '18px', fontWeight: '700', color: '#0369a1' };
const sumaPreview = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '10px', fontSize: '15px', color: '#555' };
const secInfo  = { fontSize: '13px', color: '#6b7280', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px', lineHeight: '1.6' };
const platRow  = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid #f5f5f5', gap: '12px' };
const platMain = { display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 };
const platStatus = { display: 'inline-block', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', width: 'fit-content' };
const platDescr = { fontSize: '14px', fontWeight: '500', color: '#1a1a2e' };
const platMeta  = { fontSize: '12px', color: '#9ca3af' };
const platRight = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 };
const platSuma  = { fontSize: '18px', fontWeight: '700', color: '#1a1a2e' };
const platChitanta = { fontSize: '11px', color: '#6b7280' };
const btnMic    = { padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff', color: '#555' };
const grid4     = { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '20px' };
const thRow     = { display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 2fr 1fr 1fr 1fr', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '4px', gap: '8px' };
const tdRow     = { display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 2fr 1fr 1fr 1fr', padding: '12px', borderBottom: '1px solid #f5f5f5', alignItems: 'center', gap: '8px' };
const th        = { fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase' };
const td        = { fontSize: '13px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
// ── Taxe management styles ────────────────────────────────────
const txTopBar  = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' };
const txStats   = { display: 'flex', gap: '24px' };
const txStatBox = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 20px', backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', minWidth: '72px' };
const txStatVal = { fontSize: '24px', fontWeight: '800', color: '#2d2d7e', lineHeight: 1 };
const txStatLbl = { fontSize: '12px', color: '#888', marginTop: '4px' };
const txForm    = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' };
const txHeader  = { display: 'flex', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '4px', gap: '8px' };
const txTh      = { fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px' };
const txRow     = { display: 'flex', alignItems: 'flex-start', padding: '14px 12px', borderBottom: '1px solid #f5f5f5', gap: '8px', transition: 'background .1s' };
const txTd      = { fontSize: '13px', overflow: 'hidden' };
const txCatBadge = { display: 'inline-block', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#eff6ff', color: '#2d2d7e' };
const txStatusBadge = { display: 'inline-block', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px' };
const txActBtn  = { padding: '4px 10px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff', color: '#555', whiteSpace: 'nowrap' };
